/**
 * Sections romaines — sous-dossiers `I - ETAT CIVIL`, `II - MEDCAL`… d'où l'on
 * dérive une numérotation de pièces `I-1`, `I-2`, `II-1`…
 *
 * Consommé par le mode « Numéroter par dossiers » de `RenumberPiecesDialog`.
 * Plan : `docs/features/renumber-pieces/plans/2026-07-08-numeroter-par-dossiers-romains.html`
 *
 * ── Pourquoi l'alphabet est `[IVX]` et pas `[IVXLCDM]` ────────────────────
 * `C` et `D` sont des chiffres romains valides (100, 500) ET des marqueurs de
 * section parfaitement légitimes (`C - Contrôle judiciaire`, `D - Fond`). Une
 * mesure sur les données de prod (2026-07-08) a montré que l'alphabet complet
 * produit **144 détections dont 140 fausses** : jetons `D`(67), `C`(51),
 * `V`(20), `M`(1) contre `I`/`II`/`III`/`IV` (1 chacun).
 *
 * En excluant `L C D M`, ces faux positifs ne parsent tout simplement plus —
 * le garde-fou est structurel, pas correctif. Coût : rien, car une suite
 * contiguë démarrant à `I` n'atteint jamais `L`(50) sans 50 sections.
 * `[IVX]` couvre `I..XXXIX`, très au-delà du besoin réel.
 *
 * Les marqueurs-LETTRES (`A, B, C, D`) sont **hors périmètre** : décision
 * produit du 2026-07-08, pas un oubli. Les rouvrir imposerait de trancher le
 * conflit `C`/`D` romain↔lettre.
 */

/** Valeurs unitaires de l'alphabet retenu. */
const ROMAN_UNIT: Record<string, number> = { I: 1, V: 5, X: 10 };

/** Table de ré-encodage (décroissante), bornée à l'alphabet `[IVX]`. */
const ROMAN_TABLE: ReadonlyArray<readonly [number, string]> = [
  [10, 'X'], [9, 'IX'], [5, 'V'], [4, 'IV'], [1, 'I'],
];

/** `XXXIX` = 39, plafond atteignable avec `[IVX]` seuls. */
const MAX_ROMAN = 39;

/**
 * Au-delà, ce n'est plus une sectionnation de bordereau mais autre chose
 * (arborescence de travail, codes internes…). Garde-fou de bon sens.
 */
const MAX_SECTIONS = 20;

/** Séparateurs observés après le numéro de section (prod : `-` 140, `.` 3, `:` 1). */
const SECTION_SEPARATORS = '.\\-–—:)';

/**
 * Nom de dossier sectionné : jeton romain + séparateur + un libellé non vide.
 * `I -` (sans libellé) est rejeté par le lookahead.
 */
const FOLDER_SECTION_RE = new RegExp(
  `^\\s*([IVX]+)\\s*([${SECTION_SEPARATORS}])\\s*(?=\\S)`,
  'i',
);

/**
 * Numéro de pièce romain : `I-1`, `II.3`, `IV_2`, `X)5`, `III - 2`.
 * La partie numérique est OBLIGATOIRE — un `I` nu resterait ambigu (initiale ?
 * section ?) et reste donc en bucket `other`.
 *
 * Exporté pour `parsePieceNumber` (src/index.ts).
 */
export const ROMAN_PIECE_RE = /^\s*([IVX]+)\s*[-._/):]\s*(\d+)/i;

/** Ré-encode un entier en romain canonique (alphabet `[IVX]`). */
function intToRoman(n: number): string {
  let out = '';
  let rest = n;
  for (const [value, symbol] of ROMAN_TABLE) {
    while (rest >= value) {
      out += symbol;
      rest -= value;
    }
  }
  return out;
}

/**
 * Convertit un chiffre romain **canonique** en entier, ou `null`.
 *
 * Strict par ré-encodage : `IIII`, `VV`, `IXI`, `VX` sont rejetés (ils se
 * « calculent » mais ne sont pas la forme canonique). Sans cette validation,
 * `IIII` vaudrait 4 et se trierait à égalité avec `IV` — deux pièces
 * distinctes indiscernables.
 *
 * Hors alphabet (`L C D M`) ou hors bornes `1..39` → `null`.
 *
 * @example romanToInt('IV') // 4
 * @example romanToInt('IIII') // null (non canonique)
 * @example romanToInt('D') // null (hors alphabet, cf. en-tête)
 */
export function romanToInt(input: unknown): number | null {
  if (typeof input !== 'string') return null;
  const token = input.trim().toUpperCase();
  if (token === '' || !/^[IVX]+$/.test(token)) return null;

  let total = 0;
  for (let i = 0; i < token.length; i += 1) {
    const current = ROMAN_UNIT[token[i] as string] ?? 0;
    const next = i + 1 < token.length ? (ROMAN_UNIT[token[i + 1] as string] ?? 0) : 0;
    total += current < next ? -current : current;
  }
  if (total <= 0 || total > MAX_ROMAN) return null;

  return intToRoman(total) === token ? total : null;
}

/** Une section détectée depuis un nom de sous-dossier. */
export interface FolderSection {
  /** Jeton romain normalisé en majuscules (`'I'`, `'IV'`). */
  token: string;
  /** Valeur entière correspondante (1, 4…). */
  value: number;
  /** Nom complet du sous-dossier d'origine. */
  folder: string;
}

/** Résultat d'une détection de sectionnation sur un dossier juridique. */
export interface RomanSectioning {
  /** Sections triées par valeur croissante. */
  sections: FolderSection[];
  /**
   * Caractère séparateur majoritaire des dossiers (`'-'` ou `'.'`). Reporté tel
   * quel dans le numéro de pièce, MAIS sans les espaces : le dossier s'écrit
   * `I - ETAT CIVIL`, la pièce se stocke `I-1` (constaté en prod).
   */
  separator: string;
}

/**
 * Parse un nom de sous-dossier en section romaine, ou `null`.
 *
 * @example parseFolderSection('I - ETAT CIVIL') // { token:'I', value:1, separator:'-' }
 * @example parseFolderSection('D - Fond')       // null (hors alphabet)
 * @example parseFolderSection('I -')            // null (pas de libellé)
 */
export function parseFolderSection(
  name: unknown,
): { token: string; value: number; separator: string } | null {
  if (typeof name !== 'string') return null;
  const match = FOLDER_SECTION_RE.exec(name);
  if (!match || !match[1] || !match[2]) return null;

  const value = romanToInt(match[1]);
  if (value === null) return null;

  return { token: match[1].toUpperCase(), value, separator: match[2] };
}

/**
 * Décide si les sous-dossiers d'un dossier juridique forment une sectionnation
 * romaine exploitable. Renvoie `null` dès qu'un doute subsiste.
 *
 * La détection est **globale au dossier**, jamais dossier par dossier : c'est
 * la seule façon de distinguer `IV - PROFESSIONNEL` (section) de `D - Fond`
 * (marqueur-lettre). Conditions cumulatives :
 *
 * 1. au moins 2 sous-dossiers parsent ;
 * 2. valeurs deux à deux distinctes ;
 * 3. la suite démarre à `I` et est **contiguë** (`1..N`) ;
 * 4. `N <= MAX_SECTIONS`.
 *
 * Les sous-dossiers non sectionnés (`Divers`, `Pièces annexes`) sont ignorés,
 * pas disqualifiants — l'avocat peut mélanger.
 */
export function detectRomanSectioning(folderNames: unknown): RomanSectioning | null {
  if (!Array.isArray(folderNames)) return null;

  const parsed = folderNames
    .map((name) => {
      const section = parseFolderSection(name);
      return section ? { ...section, folder: name as string } : null;
    })
    .filter((s): s is FolderSection & { separator: string } => s !== null);

  if (parsed.length < 2 || parsed.length > MAX_SECTIONS) return null;

  const values = parsed.map((s) => s.value);
  if (new Set(values).size !== values.length) return null;

  const sorted = [...parsed].sort((a, b) => a.value - b.value);
  // Suite contiguë démarrant à 1 : tue {C,D}={100,500} et les trous {I,III}.
  const contiguous = sorted.every((s, i) => s.value === i + 1);
  if (!contiguous) return null;

  const separatorCounts = new Map<string, number>();
  for (const s of parsed) {
    separatorCounts.set(s.separator, (separatorCounts.get(s.separator) ?? 0) + 1);
  }
  const separator = [...separatorCounts.entries()].sort((a, b) => b[1] - a[1])[0]![0];

  return {
    sections: sorted.map(({ token, value, folder }) => ({ token, value, folder })),
    separator,
  };
}
